import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Alert,
  Link as MuiLink,
  Tabs,
  Tab,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { authService } from '../../services/authService';
import { profileService } from '../../services/profileService';
import { supabase } from '../../config/supabase';
import TrademarkFooter from '../common/TrademarkFooter';
import { TicketForm } from '../crm/TicketForm';

type LoginMode = 'password' | 'magiclink';

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isHelpDialogOpen, setIsHelpDialogOpen] = useState(false);
  const [loginMode, setLoginMode] = useState<LoginMode>('password');
  const [magicLinkSent, setMagicLinkSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      if (data.session) {
        // Get user's role from profiles table
        const role = await profileService.getUserRole();
        
        if (!role) {
          throw new Error('Unable to determine user role');
        }

        // Navigate based on role
        if (role === 'admin') {
          navigate('/dashboard');
        } else if (role === 'new_hire') {
          navigate('/onboarding');
        } else if (role === 'owner') {
          navigate('/leaderdashboard');
        } else if (role === 'ticketmaster') {
          navigate('/tickets');
        } else {
          // Handle any other roles or default case
          setError('Invalid user role');
          await supabase.auth.signOut();
        }
      }
    } catch (err: any) {
      setError(err.message || 'Failed to sign in');
    } finally {
      setIsLoading(false);
    }
  };

  const handleMagicLinkSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);
    try {
      await authService.signInWithOtp(email);
      setMagicLinkSent(true);
    } catch (err: any) {
      setError(err.message || 'Failed to send magic link');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            padding: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <Typography component="h1" variant="h5" gutterBottom>
            Admin Login
          </Typography>
          <Tabs
            value={loginMode}
            onChange={(_, v: LoginMode) => {
              setLoginMode(v);
              setError(null);
              setMagicLinkSent(false);
            }}
            sx={{ width: '100%', mb: 1 }}
          >
            <Tab label="Password" value="password" />
            <Tab label="Magic link" value="magiclink" />
          </Tabs>
          {error && (
            <Alert severity="error" sx={{ width: '100%', mt: 2 }}>
              {error}
            </Alert>
          )}
          {magicLinkSent ? (
            <Alert severity="success" sx={{ width: '100%', mt: 2 }}>
              Check your email for the sign-in link. Click it to sign in (no password needed).
            </Alert>
          ) : loginMode === 'magiclink' ? (
            <Box component="form" onSubmit={handleMagicLinkSubmit} sx={{ mt: 1, width: '100%' }}>
              <TextField
                margin="normal"
                required
                fullWidth
                id="email-magic"
                label="Email Address"
                name="email"
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
                disabled={isLoading}
              >
                {isLoading ? 'Sending link...' : 'Send magic link'}
              </Button>
              <Box sx={{ textAlign: 'center' }}>
                <MuiLink
                  component="button"
                  variant="body2"
                  onClick={() => setIsHelpDialogOpen(true)}
                  sx={{ cursor: 'pointer' }}
                >
                  Help
                </MuiLink>
              </Box>
            </Box>
          ) : (
            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1, width: '100%' }}>
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="Email Address"
                name="email"
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="Password"
                type="password"
                id="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
                disabled={isLoading}
              >
                {isLoading ? 'Signing in...' : 'Sign In'}
              </Button>
              <Box sx={{ textAlign: 'center' }}>
                <MuiLink
                  component={RouterLink}
                  to="/forgot-password"
                  variant="body2"
                  sx={{ mr: 2 }}
                >
                  Forgot password?
                </MuiLink>
                <MuiLink
                  component="button"
                  variant="body2"
                  onClick={() => setIsHelpDialogOpen(true)}
                  sx={{ cursor: 'pointer' }}
                >
                  Help
                </MuiLink>
              </Box>
              <Box sx={{ textAlign: 'center', mt: 1 }}>
                <MuiLink component={RouterLink} to="/login" variant="body2">
                  Back to New Hire Login
                </MuiLink>
              </Box>
            </Box>
          )}
        </Paper>
      </Box>
      <TrademarkFooter />
      <TicketForm 
        open={isHelpDialogOpen} 
        onClose={() => setIsHelpDialogOpen(false)} 
      />
    </Container>
  );
};

export default LoginPage; 